import { useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, FileText, Loader2, Lock, Receipt, Shield, Users } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "@/lib/api";
import { useBilling, startCheckout, confirmCharge, money } from "@/components/Billing";
import { PriceQuote } from "@/components/PriceQuote";

const NavLinks = () => (
  <>
    <Link to="/billing" data-testid="billing-history-link" className="flex items-center gap-1 hover:text-[var(--cyan)] transition-colors"><Receipt size={12} /> History</Link>
    <Link to="/team" data-testid="team-link" className="flex items-center gap-1 hover:text-[var(--cyan)] transition-colors"><Users size={12} /> Team</Link>
  </>
);

export const BillingStrip = () => {
  const { billing, reload } = useBilling();
  const [busy, setBusy] = useState(false);
  if (!billing) return null;
  if (billing.is_admin) return (
    <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
      <span className="text-[var(--emerald)]" data-testid="billing-admin">Admin · free access</span>
      <Link to="/admin" data-testid="admin-panel-link" className="flex items-center gap-1 text-[var(--amber)] hover:underline"><Shield size={12} /> Admin panel</Link><NavLinks />
    </div>
  );
  const legacy = billing.legacy_subscription;
  const cancelLegacy = async () => {
    if (!window.confirm("Cancel your old $200/month subscription? It's no longer needed - drafts are pay-per-sq-ft.")) return;
    setBusy(true);
    try { await api.post("/billing/cancel"); await reload(); toast.success("Old subscription will not renew"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]" data-testid="billing-strip">
      <span className="flex items-center gap-1.5" data-testid="billing-status"><CreditCard size={13} className="text-[var(--cyan)]" />Pay per draft · $1 / sq ft</span>
      {billing.card && (
        <button data-testid="update-card-btn" onClick={() => startCheckout("/billing/card/setup").catch((e) => toast.error(errMsg(e)))} className="hover:text-[var(--cyan)]">
          {billing.card.brand} •••• {billing.card.last4} · update
        </button>
      )}
      {legacy && <button data-testid="cancel-legacy-sub-btn" disabled={busy} onClick={cancelLegacy} className="text-[var(--amber)] hover:underline">Cancel old $200/mo plan</button>}
      <NavLinks />
      {busy && <Loader2 size={12} className="animate-spin" />}
    </div>
  );
};

export const LockedExports = ({ project, onPaid }) => {
  const { billing } = useBilling();
  const [busy, setBusy] = useState(false);
  const sqft = project.unlock_sqft || 0;
  const [prev, setPrev] = useState(false);
  const [pq, setPq] = useState({ code: "", total: sqft });
  const pay = async () => {
    if (pq.total > 0 && !confirmCharge(billing, pq.total)) return;
    setBusy(true);
    try {
      const data = await startCheckout("/billing/draft-checkout", { project_id: project.project_id, code: pq.code });
      if (data.paid) { toast.success(`Charged ${money(data.amount)} - dimensions unlocked`); setBusy(false); onPaid(); }
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="border border-[var(--amber)]/50 bg-[var(--amber)]/5 p-4 space-y-3" data-testid="locked-exports">
      <p className="flex items-center gap-2 font-display font-semibold"><Lock size={15} className="text-[var(--amber)]" /> Dimensions locked</p>
      <p className="text-xs text-[var(--muted)]">Dimensions, room sizes, Rhino sync and downloads (.3dm, script, PDF) unlock once this {Math.round(project.plan.total_area).toLocaleString()} sq ft draft is paid for.</p>
      <PriceQuote sqft={sqft} onChange={setPq} compact />
      <button data-testid="unlock-draft-btn" onClick={pay} disabled={busy}
        className="w-full py-2.5 text-sm font-semibold bg-[var(--amber)] text-[var(--bg)] hover:brightness-110 transition-[filter] disabled:opacity-50 flex items-center justify-center gap-2">
        {busy && <Loader2 size={14} className="animate-spin" />} Unlock for {money(pq.total)}{billing?.card ? ` · •••• ${billing.card.last4}` : ""}
      </button>
      <button data-testid="download-preview-pdf-btn" disabled={prev}
        onClick={async () => { setPrev(true); try { await downloadFile(`/projects/${project.project_id}/export/preview-pdf`, `${project.title.replace(/[^\w-]+/g, "_")}-preview.pdf`); toast.success("Preview PDF downloaded"); } catch (e) { toast.error(errMsg(e)); } setPrev(false); }}
        className="w-full py-2.5 text-sm border border-[var(--line)] hover:border-[var(--amber)] transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
        {prev ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Watermarked preview PDF · free
      </button>
      <p className="text-[11px] text-[var(--muted)]">Share the layout with clients before paying - no dimensions or sizes, not to scale.</p>
    </div>
  );
};

export const PayCard = ({ project, onDiscard, onPaid }) => {
  const { billing } = useBilling();
  const [busy, setBusy] = useState(false);
  const pend = project.pending_plan;
  const sqft = pend ? pend.extra_sqft : project.requested_sqft;
  const [pq, setPq] = useState({ code: "", total: sqft || 0 });
  const pay = async () => {
    if (pq.total > 0 && !confirmCharge(billing, pq.total)) return;
    setBusy(true);
    try {
      const data = await startCheckout("/billing/draft-checkout", { project_id: project.project_id, code: pq.code });
      if (data.paid) { toast.success(data.amount ? `Charged ${money(data.amount)} to your card on file` : "Covered by your discount - no charge"); setBusy(false); onPaid(); }
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="m-4 border border-[var(--amber)]/50 bg-[var(--amber)]/5 p-4 space-y-3" data-testid="pay-card">
      <p className="text-sm">{pend && !project.plan
        ? <>The AI's first draft came out at <b>{Math.round(pend.plan.total_area).toLocaleString()} sq ft</b>, above the {Math.round(project.licensed_sqft || 0).toLocaleString()} sq ft you licensed. License the extra {sqft.toLocaleString()} sq ft to apply it.</>
        : pend
        ? <>The latest revision grows this home to <b>{Math.round(pend.plan.total_area).toLocaleString()} sq ft</b>. License the extra {sqft.toLocaleString()} sq ft to apply it.</>
        : <>This draft is licensed at <b>{(sqft || 0).toLocaleString()} sq ft</b> × $1. Pay to let the AI start drafting.</>}</p>
      <PriceQuote sqft={sqft} onChange={setPq} compact />
      <div className="flex gap-2">
        <button data-testid="pay-draft-btn" onClick={pay} disabled={busy}
          className="flex-1 py-2.5 text-sm font-semibold bg-[var(--amber)] text-[var(--bg)] hover:brightness-110 transition-[filter] disabled:opacity-50 flex items-center justify-center gap-2">
          {busy && <Loader2 size={14} className="animate-spin" />} Pay {money(pq.total)}{billing?.card ? ` · •••• ${billing.card.last4}` : ""}
        </button>
        {pend && <button data-testid="discard-pending-btn" onClick={onDiscard} className="px-3 text-sm border border-[var(--line)] text-[var(--muted)] hover:text-white transition-colors">Discard</button>}
      </div>
    </div>
  );
};
